import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Modal from 'react-modal';

import { Button, Typography } from 'components/common';
import Navbar from 'components/Owner/Navbar';
import Login from 'components/Owner/Login';
import SignupFlow from 'components/Owner/SignupFlow';
import ForgotPassword from 'components/Owner/ForgotPassword';
import ForgotPasswordConfirm from 'components/Owner/ForgotPassword/ForgotPasswordConfirm';
import Home from 'components/Owner/Home';
import Cars from 'components/Owner/Cars';
import EditCar from 'components/Owner/Cars/EditCar';
import ConnectCars from 'components/Owner/ConnectCars';
import Calendar from 'components/Owner/Calendar';
import ReservationsDashboard from 'components/Owner/Reservations';
// import CreateReservation from 'components/Owner/Reservations/CreateReservation';
import EditReservation from 'components/Owner/Reservations/EditReservation';
import Payouts from 'components/Owner/Payouts';
import Payout from 'components/Owner/Payouts/Payout';
import Profile from 'components/Owner/Profile';
import ReportBug from 'components/ReportBug';
import Partner from 'components/Owner/Partner';
import Referrals from 'components/Owner/Referrals';

import { usePartnerConfig } from 'hooks';
import { ownerActions, ownerSelectors } from './redux/slices';
// import { useDarkMode } from './hooks';
import { IOwner } from './types';
import { OWNER_TOKEN_STORAGE } from './constants';

import 'App.scss';

function RequireAuth({ children }: { children?: any }) {
  const owner = useSelector(ownerSelectors.selectOwner, shallowEqual) as IOwner;
  const location = useLocation();

  if (!owner && !localStorage.getItem(OWNER_TOKEN_STORAGE)) {
    return <Navigate to="/owner/login" state={{ from: location }} />;
  }

  if (!location.pathname.includes('owner/signup/flow') && owner && !owner.setup_complete) {
    return <Navigate to="/owner/signup/flow" state={{ from: location }} />;
  }

  if (
    owner
    && owner.is_corporate
    && !/^\/owner\/cars\/[^/]+\/edit$/.test(location.pathname)
    && location.pathname !== '/owner/reservations'
    && !/^\/owner\/reservations\/[^/]+\/edit$/.test(location.pathname)
  ) {
    return <Navigate to="/owner/reservations" state={{ from: location }} />;
  }

  return children || <Outlet />;
}

const OwnerRouter = () => {
  const dispatch = useDispatch();
  const owner = useSelector(ownerSelectors.selectOwner) as IOwner;
  const shownOrionModal = useSelector(ownerSelectors.selectShownOrionModal) as boolean;

  useEffect(() => {
    if (owner?.id) {
      mixpanel.identify(owner.id.toString());

      mixpanel.people.set({
        $name: owner.firstname && owner.lastname && `${owner.firstname} ${owner.lastname}`,
        $email: owner.email,
        $phone: owner.phone,
      });
    }
  }, [owner]);

  useEffect(() => {
    if (localStorage.getItem(OWNER_TOKEN_STORAGE) && !owner) {
      dispatch(ownerActions.getOwner.base());
    }
  }, [owner]);

  // useDarkMode();
  usePartnerConfig();

  return (
    <>
      <Navbar />
      <Routes>
        <Route path="signup" element={<Login showAsSignup />} />
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="forgot-password-confirm" element={<ForgotPasswordConfirm />} />
        <Route path="report-bug" element={<ReportBug />} />
        <Route path="cars/connect" element={<ConnectCars />} />

        <Route
          element={<RequireAuth />}
        >
          <Route path="signup/flow" element={<SignupFlow />} />
          <Route path="" element={<Home />} />
          <Route path="home" element={<Home />} />
          <Route path="cars" element={<Cars />} />
          <Route path="cars/:carId/edit" element={<EditCar />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="reservations" element={<ReservationsDashboard />} />
          {/* <Route path="reservations/create" element={<CreateReservation />} /> */}
          <Route path="reservations/:reservationId/edit" element={<EditReservation />} />
          <Route path="payouts" element={<Payouts />} />
          <Route path="payouts/:payoutId" element={<Payout />} />
          <Route path="partner" element={<Partner />} />
          <Route path="profile" element={<Profile />} />
          <Route path="referrals" element={<Referrals />} />
        </Route>
      </Routes>

      <Modal
        ariaHideApp={false}
        isOpen={owner && !shownOrionModal}
        style={{
          overlay: {
            zIndex: 1000,
          },
          content: {
            zIndex: 1000,
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            padding: '29px',
            backgroundColor: 'white',
            borderRadius: '20px',
            maxWidth: '90%',
            width: '550px',
          },
        }}
        onRequestClose={() => {
          dispatch(ownerActions.setShownOrionModal.base(true));
        }}
      >
        <div className="d-flex justify-content-end">
          <div className="d-flex w-100 align-items-center justify-content-center">
            <img
              src="/images/orion/eon-partners-logo-2x.png"
              alt="orion redirect"
              style={{ width: '160px', height: '100%', objectFit: 'contain' }}
            />
          </div>

          <Button
            onClick={() => {
              dispatch(ownerActions.setShownOrionModal.base(true));
            }}
            style={{
              position: 'absolute',
              right: 20,
              width: '25px',
              height: '25px',
              padding: 0,
              boxShadow: 'none',
            }}
          >
            X
          </Button>
        </div>

        <div className="d-flex flex-column gap-1 mt-4">
          <img
            src={window.innerWidth < 720 ? '/images/orion/orion-redirect-mobile.png' : '/images/orion/orion-redirect-desktop.png'}
            alt="orion redirect"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />

          <div className="mt-2">
            <Typography variant="body2" style={{ color: '#D400F7' }}>
              Introducing
            </Typography>
          </div>

          <div>
            <Typography variant="h2">
              The All-New Owners Experience
            </Typography>
          </div>

          <div>
            <Typography variant="body2" style={{ color: 'rgba(18, 18, 22, 0.50)' }}>
              Your redesigned partner portal is here.
              Track earnings, manage your fleet, and grow your business.
              All in one powerful platform.
            </Typography>
          </div>

          <div
            className="mt-3 d-flex w-100 gap-2
            flex-row align-items-center  justify-content-flex-end"
          >
            <Button
              onClick={() => {
                dispatch(ownerActions.setShownOrionModal.base(true));
              }}
              style={{
                outline: 'none',
                boxShadow: 'none',
                width: '150px'
              }}
            >
              Maybe Later
            </Button>

            <Button
              onClick={() => {
                dispatch(ownerActions.setShownOrionModal.base(true));
                window.location.href = `${process.env.REACT_APP_ORION_URI}/dashboard`;
              }}
              variant="primary"
              style={{ width: '150px' }}
            >
              Explore Now
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OwnerRouter;
